import { useEffect, useState } from "react";
import { Button, Dialog, DialogClose, InlineAlert, TextField } from "@/components";
import { displayCode, extractLotCode } from "./qr/codes";
import { useQrScanner } from "./qr/useQrScanner";
import styles from "./QrScanDialog.module.css";

export interface QrScanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  /** Called with the scanned (or typed) lot code, normalized: "T03024S". */
  onCode: (code: string) => void;
}

/** Scan a vial's QR code with the camera (or type the code printed under it). */
export function QrScanDialog({
  open,
  onOpenChange,
  title = "Scan the vial",
  description = "Hold the label's QR code in front of the camera.",
  onCode,
}: QrScanDialogProps) {
  const [typed, setTyped] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const { videoRef, status, error, start, stop } = useQrScanner((text) => {
    const code = extractLotCode(text);
    if (code) {
      onCode(code);
      onOpenChange(false);
    } else {
      setProblem("That QR code isn't a lot label from this app. Try another, or type the code.");
      setAttempt((count) => count + 1);
    }
  });

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => void start(), 50);
    return () => {
      window.clearTimeout(timer);
      stop();
    };
  }, [open, attempt, start, stop]);

  function submitTyped() {
    const code = extractLotCode(typed);
    if (!code) {
      setProblem("Lot codes have 7 characters, like T030-24S. Check the code under the QR.");
      return;
    }
    onCode(code);
    setTyped("");
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={title} description={description} footer={<DialogClose />}>
      <div className={styles.body}>
        <div className={styles.viewfinder} data-status={status}>
          <video ref={videoRef} className={styles.video} muted playsInline aria-label="Camera preview" />
          {status === "starting" ? <span className={styles.overlay}>Starting the camera…</span> : null}
        </div>
        {error ? <InlineAlert tone="warning" title={error} /> : null}
        {problem ? <InlineAlert tone="warning" title={problem} /> : null}
        <form
          className={styles.manual}
          onSubmit={(event) => {
            event.preventDefault();
            submitTyped();
          }}
        >
          <TextField
            label="Or type the code"
            value={typed}
            onValueChange={(value) => {
              setTyped(value);
              setProblem(null);
            }}
            mono
            placeholder={displayCode("T03024S")}
            autoComplete="off"
          />
          <Button type="submit" disabled={!typed.trim()}>
            Check code
          </Button>
        </form>
      </div>
    </Dialog>
  );
}
